import { ControlledSelect } from '@packages/modules/src/ControlledSelect/ControlledSelect';
import { SelectContent, SelectItem } from '@packages/shared/src/ui/Select/Select';
import { CardContent, CardHeader, CardTitle, Card } from '@packages/shared/src/ui/Card/Card';
import { useEffect, type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Controller, useFormContext } from 'react-hook-form';
import { InputCurrency } from '@packages/shared/src/ui/Input/InputCurrency';
import { RadioGroup } from '@packages/shared/src/ui/RadioGroup/RadioGroup';
import { Radio } from '@packages/shared/src/ui/Radio/Radio';
import { Popover, PopoverContent, PopoverTrigger } from '@packages/shared/src/ui/Popover/Popover';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { generalFormMessages } from './messages';
import type { CreditCheckFormValues } from './schema';
import { numberOfChildrenOptions, numberOfPeopleOptions } from './schema';

type LivingSituationCardProps = {};
export const messages = defineMessages({
  cardTitle: {
    id: 'CreditCheckForm.LivingSituationCard.CardTitle',
    defaultMessage: 'Thuissituatie',
  },
  numberOfPeople: {
    id: 'CreditCheckForm.LivingSituationCard.NumberOfPeople',
    defaultMessage: 'Aantal personen per gezin/huishouden *',
  },
  numberOfChildren: {
    id: 'CreditCheckForm.LivingSituationCard.NumberOfChildren',
    defaultMessage: 'Aantal kinderen onder de 18 jaar *',
  },
  monthlyCosts: {
    id: 'CreditCheckForm.LivingSituationCard.MonthlyCosts',
    defaultMessage: 'Maandelijkse woonlasten *',
  },
  typeOfPropertyOwnership: {
    id: 'CreditCheckForm.LivingSituationCard.TypeOfProperty',
    defaultMessage: 'Type woning *',
  },
  rentalRadio: {
    id: 'CreditCheckForm.LivingSituationCard.Rental',
    defaultMessage: 'Huur',
  },
  ownershipRadio: {
    id: 'CreditCheckForm.LivingSituationCard.Ownership',
    defaultMessage: 'Koop',
  },
  livingWithParents: {
    id: 'CreditCheckForm.LivingSituationCard.LivingWithParents',
    defaultMessage: 'Ik woon thuis bij mijn ouder(s) / verzorger(s)',
  },
  monthlyIncomeInfo: {
    id: 'CreditCheckForm.LivingSituationCard.MonthlyIncomeInfo',
    defaultMessage:
      'Dies ist der Betrag ohne Mietzuschuss oder Steuererstattung. Runden Sie diesen Betrag auf ganze Euro auf. Wenn Sie zu Hause wohnen, geben Sie den Betrag an, den Sie für die Verpflegung zahlen. Vergessen Sie nicht, uns Ihre neue Adresse mitzuteilen, wenn Sie umziehen.',
  },
});
export const LivingSituationCard: FC<LivingSituationCardProps> = () => {
  const {
    register,
    formState: { errors },
    watch,
    control,
    setValue,
    clearErrors,
    unregister,
  } = useFormContext<CreditCheckFormValues>();
  const { formatMessage } = useIntl();
  const currNumberOfPeople = Number(watch('firstPartForm.numberOfPeople'));
  const currNumberOfChildren = Number(watch('firstPartForm.numberOfChildren'));
  useEffect(() => {
    currNumberOfChildren >= 0 && clearErrors('firstPartForm.numberOfChildren');
    currNumberOfPeople >= 0 && clearErrors('firstPartForm.numberOfPeople');
    if (currNumberOfPeople <= 1) {
      setValue('firstPartForm.numberOfChildren', '0');
      clearErrors('firstPartForm.numberOfChildren');
    }
    if (currNumberOfPeople === 0) {
      unregister('firstPartForm.typeOfPropertyOwnership');
    }
    if (currNumberOfPeople <= currNumberOfChildren && currNumberOfPeople >= 1) {
      setValue('firstPartForm.numberOfChildren', '0');
      clearErrors('firstPartForm.numberOfChildren');
    }
  }, [currNumberOfChildren, currNumberOfPeople, register, setValue, clearErrors, unregister]);

  const showTypeOfPropertyOwnership = currNumberOfPeople >= 1;
  return (
    <Card data-testid="living-situation-card">
      <CardHeader hasDivider className="py-1">
        <CardTitle>{formatMessage(messages.cardTitle)}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 py-1.5 md:grid md:grid-cols-2 md:pb-2">
        <ControlledSelect
          control={control}
          controllerName="firstPartForm.numberOfPeople"
          label={formatMessage(messages.numberOfPeople)}
          error={!!errors.firstPartForm?.numberOfPeople?.message}
          helperText={errors.firstPartForm?.numberOfPeople?.message}
        >
          <SelectContent>
            {numberOfPeopleOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {option === '0' ? formatMessage(messages.livingWithParents) : option}
              </SelectItem>
            ))}
          </SelectContent>
        </ControlledSelect>
        <ControlledSelect
          control={control}
          controllerName="firstPartForm.numberOfChildren"
          label={formatMessage(messages.numberOfChildren)}
          disabled={Number.isNaN(currNumberOfPeople) || currNumberOfPeople <= 1}
          error={!!errors.firstPartForm?.numberOfChildren?.message}
          helperText={errors.firstPartForm?.numberOfChildren?.message}
        >
          <SelectContent>
            {numberOfChildrenOptions.slice(0, currNumberOfPeople).map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </ControlledSelect>
        <div className="flex items-center">
          <InputCurrency
            {...register('firstPartForm.monthlyCosts')}
            label={formatMessage(messages.monthlyCosts)}
            containerClassName="w-full"
            placeholder={formatMessage(generalFormMessages.inputPlaceholder)}
            error={!!errors.firstPartForm?.monthlyCosts?.message}
            helperText={errors.firstPartForm?.monthlyCosts?.message}
            currencyPosition="left"
          />
          <Popover>
            <PopoverTrigger>
              <div
                className={cn(
                  'icon-emp-information-circle ml-1.5 size-4',
                  errors?.secondPartForm?.monthlyIncome?.message ? 'mt-0' : 'mt-2.5',
                )}
              />
            </PopoverTrigger>
            <PopoverContent>
              <p className="text-size-body2 w-[20rem] p-2">
                {formatMessage(messages.monthlyIncomeInfo)}
              </p>
            </PopoverContent>
          </Popover>
        </div>
        {showTypeOfPropertyOwnership && (
          <Controller
            control={control}
            name="firstPartForm.typeOfPropertyOwnership"
            render={({ field }) => (
              <RadioGroup
                {...field}
                label={formatMessage(messages.typeOfPropertyOwnership)}
                className="md:col-span-2 md:row-start-3"
                error={!!errors.firstPartForm?.typeOfPropertyOwnership?.message}
                notificationText={errors.firstPartForm?.typeOfPropertyOwnership?.message}
              >
                <Radio
                  onChange={field.onChange}
                  value="rental"
                  label={formatMessage(messages.rentalRadio)}
                  id="rental"
                  name="typeOfPropertyOwnership"
                  checked={field.value === 'rental'}
                />
                <Radio
                  onChange={field.onChange}
                  value="ownership"
                  label={formatMessage(messages.ownershipRadio)}
                  id="ownership"
                  name="typeOfPropertyOwnership"
                  checked={field.value === 'ownership'}
                />
              </RadioGroup>
            )}
          />
        )}
      </CardContent>
    </Card>
  );
};
